import { useEffect, useRef, useState } from 'react'
import { equals } from 'ramda'

import { Request } from 'app/common/api/requests'
import { api } from 'app/api'
import { Convertions } from 'app/common/api/Api'
import shallowEqual from 'app/common/lib/shallowEqual'

const compareMethods = {
  shallow: shallowEqual,
  equals,
}

export type Options<Data, P = undefined> = {
  params: Partial<P>
  autoload?: boolean
  autoupdate?: boolean
  requestValue: Request<P, Data, any> // TODO
  error?: string | ((err: Error) => string)
  convertions: Convertions<P, Data>
  beforeRequest?: () => Partial<P>
  isParamsValid?: (params: any) => params is P
  defaultState: Data
  compare?: keyof typeof compareMethods
  initialLoading?: boolean
}

const defaultParams = {
  params: null,
  autoload: true,
  autoupdate: true,
  isParamsValid: () => true,
  error: 'Произошла неизвестная ошибка',
  compare: 'equals',
  initialLoading: true,
}

const emptyRequest: <T>() => Promise<T | null> = <T>() => Promise.resolve(null as T | null)

export type CommonDataOptions<State, Params = undefined> = Omit<
  Options<State, Params>,
  'requestValue' | 'convertions' | 'defaultState'
>

export function useCommonDataProvider<Data, P = undefined>(options: Options<Data, P>) {
  const {
    params,
    autoload,
    autoupdate,
    requestValue,
    error,
    convertions,
    beforeRequest,
    defaultState,
    compare,
    isParamsValid,
    initialLoading,
  } = { ...defaultParams, ...options }

  const inProcess = useRef(false)

  const [loading, setLoading] = useState(initialLoading)
  const [data, setData] = useState<Data>(defaultState)

  const prevParamsRef = useRef(params)
  const requestRef = useRef<(params_?: Partial<P>) => Promise<Data | null>>(emptyRequest)

  const request = async (params_?: Partial<P>) => {
    if (inProcess.current) return null
    inProcess.current = true
    setLoading(true)
    const additionalParams: Partial<P> = beforeRequest ? beforeRequest() : {}
    const paramsAll = {
      ...(params || {}),
      ...additionalParams,
      ...(params_ || {}),
    } as P

    if (!isParamsValid(paramsAll)) {
      setLoading(false)
      inProcess.current = false
      return null
    }

    const res = await api.request<P, Data>(requestValue, {
      error,
      params: paramsAll,
      convertions,
    })

    if (res) {
      setData(res)
      setLoading(false)
      inProcess.current = false
      return res
    }
    inProcess.current = false
    setLoading(false)
    return null
  }
  requestRef.current = request

  useEffect(() => {
    if (!compareMethods[compare](prevParamsRef.current, params)) {
      prevParamsRef.current = params
      if (autoupdate) request()
    }
  })

  useEffect(() => {
    if (autoload) {
      request()
    }
  }, [autoload]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    loading,
    requestRef,
    request,
    setData,
  }
}
